import Button from "components/Button";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import Modal, { ModalProps } from "components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { CONFORMITY_LIST } from "./EnvironmentalIndicators";
import { epdEditorStore } from "../EPDEditorStore";
import FileUploader from "components/FileUploader";
import { IconCSVFile } from "utils";
import ConfirmModal from "components/ConfirmModal";
import LoadingIcon from "components/LoadingIcon";
import useToast from "hooks/useToast";
import { ParsedIndicators } from "api-client";
import { observer } from "mobx-react-lite";
import { IconDownload, IconSparkles } from "@tabler/icons-react";

interface ImportModalProps extends ModalProps {
  mode: string;
  openConfirm?: boolean;
  file?: File;
  conformity?: string;
}

const CSV_FILE_TYPES = ".xlsx, .csv, .xls";

const ImportModal = (props: ImportModalProps) => {
  const { t } = useTranslation();
  const [selectedConformity, setSelectedConformity] = React.useState<string>(
    CONFORMITY_LIST[0].id
  );
  const toast = useToast();
  const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(null);
  const [inProgress, setInProgress] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  React.useEffect(() => {
    if (!props.open) {
      setSelectedConformity(CONFORMITY_LIST[0].id);
      setSelectedFiles(null);
      setShowConfirm(false);
      setInProgress(false);
    }
  }, [props.open]);

  React.useEffect(() => {
    if (props.openConfirm && props.file) {
      setSelectedFiles([props.file]);
      setShowConfirm(true);
    }
  }, [props.openConfirm]);

  const onChangeConformity = (value: BGItem | undefined) => {
    value && setSelectedConformity(value.id);
  };

  const addToUploadFiles = (files: File[]) => {
    setSelectedFiles(files);
  };

  const getIndicators = async () => {
    if (!selectedFiles?.length) return;
    setShowConfirm(false);
    setInProgress(true);
    try {
      let generatedIndicators: ParsedIndicators | undefined;
      const conformity = props.conformity ?? selectedConformity;

      if (props.mode === "AI") {
        generatedIndicators = await epdEditorStore.fetchIndicatorsWithAI(
          conformity,
          selectedFiles
        );
      } else {
        generatedIndicators = await epdEditorStore.fetchIndicatorsFromFile(
          conformity,
          selectedFiles[0]
        );
      }

      epdEditorStore.setImportedIndicators({
        ...generatedIndicators,
        conformity_name: conformity,
      });

      toast(
        t("EPDEditor.importSuccess", { count: generatedIndicators.count }),
        "success"
      );
    } catch (e) {
      toast(
        props.mode === "AI" ? t("EPDEditor.AIError") : t("EPDEditor.CSVError"),
        "warning"
      );
    } finally {
      setInProgress(false);
      props.close();
    }
  };

  function showFooter(): React.ReactNode {
    if (inProgress) return null;
    return (
      <div className="bg-gray-50 w-full flex justify-end">
        <Button
          width="fit-content"
          disable={inProgress || !selectedFiles?.length}
          onClick={() => setShowConfirm(true)}
        >
          {t("EPDEditor.importButton")}
        </Button>
      </div>
    );
  }

  function showInProgressView() {
    return (
      <div className="p-8 flex flex-col items-center justify-center">
        <LoadingIcon className="w-[80px] h-[80px]" />
        {props.mode === "AI" && (
          <span className="text-xs font-medium text-gray-500 mt-4">
            {t("EPDEditor.AIAnalysing")}
          </span>
        )}
      </div>
    );
  }

  async function getTemplate() {
    await epdEditorStore.fetchCSVTemplate(selectedConformity);
  }

  if (showConfirm || (props.openConfirm && !inProgress)) {
    return (
      <ConfirmModal
        type="warning"
        confirmButtonTitle={t("EPDEditor.overwriteIndicatorsButton")}
        onConfirm={() => getIndicators()}
        open={showConfirm}
        close={() => setShowConfirm(false)}
        title={t("EPDEditor.overwriteIndicatorsTitle")}
        descriptionMesage={t("EPDEditor.overwriteIndicatorsDesc")}
      />
    );
  }

  return (
    <Modal
      isOpen={props.open}
      setIsOpen={props.close}
      title={
        <div className="flex items-center">
          {props.mode === "AI" && <IconSparkles className="mr-2" />}
          {props.mode === "CSV"
            ? t("EPDEditor.CSVImportTitle")
            : t("EPDEditor.AIImportTitle")}
        </div>
      }
      containerClassName="min-w-[768px]"
      closeButton
      footer={showFooter()}
    >
      {inProgress ? (
        showInProgressView()
      ) : (
        <div className="p-6">
          <div className="text-base text-gray-800 font-semibold mb-2">
            {t("EPDEditor.step1Title")}
          </div>
          <div className="text-xs font-normal text-gray-700 mb-4">
            {t("EPDEditor.step1Subtitle")}
          </div>

          <ButtonGroups
            items={CONFORMITY_LIST}
            onChange={onChangeConformity}
            current={CONFORMITY_LIST.find(
              (item) => item.id === selectedConformity
            )}
            containerClassName="w-full mb-4"
            confirmTitle={t("productEditor.confirmModalTitle")}
            confirmButtonTitle={t("productEditor.changeReferenceUnit")}
            confirmDescriptionMesage={t(
              "productEditor.changeReferenceUnitDesc"
            )}
            required
            optionClsssName="h-[36px]"
            size="middle"
          />

          <div className="text-base text-gray-800 font-semibold mb-2">
            {props.mode === "CSV"
              ? t("EPDEditor.step2CSVTitle")
              : t("EPDEditor.step2AITitle")}
          </div>
          <div className="text-xs font-normal text-gray-700 mb-4">
            {props.mode === "CSV"
              ? t("EPDEditor.step2CSVSubtitle")
              : t("EPDEditor.step2AISubtitle")}
          </div>
          {props.mode === "CSV" && (
            <div className="flex justify-center mb-4">
              <Button
                onClick={getTemplate}
                type="link"
                width="fit-content"
                leadingIcon={<IconDownload />}
                className="text-indigo-700 hover:!text-indigo-500 !text-base font-medium"
              >
                {t("EPDEditor.downloadTemplate")}
              </Button>
            </div>
          )}
          {props.mode === "CSV" ? (
            <FileUploader
              name="files"
              onChange={addToUploadFiles}
              setInProgress={setInProgress}
              maxFileSize={80 * 1024 * 1024}
              fileTypes={[CSV_FILE_TYPES]}
              fileIcon={IconCSVFile}
            />
          ) : null}
        </div>
      )}
    </Modal>
  );
};

export default observer(ImportModal);
